import { useEffect, useCallback, useState, useMemo } from 'react'
import Big from 'big.js'
import { shallowEqual, useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useGridApiRef } from '@mui/x-data-grid-pro_modern'
import { Tooltip, IconButton, Stack, Typography, Button } from '@mui/material_modern'
import { styled } from '@mui/system'
import TimerSharpIcon from '@mui/icons-material_modern/TimerSharp'
import LockIcon from '@mui/icons-material_modern/Lock'
import DeleteIcon from '@mui/icons-material_modern/Delete'
import PrintIcon from '@mui/icons-material_modern/Print'

import { useGetCustomerQuery } from 'services/customers'
import { setCriticalDataLoadingError } from 'state/loading'
import { useUpdatePaymentMutation } from 'services/payments'

import BasicDataGrid from 'components/generic/datagrid'
import StyledLink from 'components/generic/typography/StyledLink'
import PrintCheckBlade from 'components/containers/bills/components/PrintCheckBlade'

import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'
import { useRefetchAfterError } from 'utils/custom-hooks/useRefetchAfterError'
import { setDecimalPrecision, generateLockTooltip, dispatchSnackbar } from 'utils/helpers'
import { paymentHoldInEffect } from 'components/containers/inbound-tickets/helpers/grid-validations'
import { materialsHasTareRequiredAndMissing } from 'components/containers/bills/utils'
import { MATERIAL_TYPE_RULES, ROUNDING_PRECISIONS, PDF_TYPE } from 'utils/constants'

const pinnedRows = ['subtotal', 'tax', 'total']
const excludePinnedRowsSort = (v1, v2, row1, row2) => {
  const isPinned1 = pinnedRows.includes(row1.id)
  const isPinned2 = pinnedRows.includes(row2.id)
  if (isPinned1 || isPinned2) return 0
  return v1 - v2 // regular sort
}

const GridStyling = styled('div')(({ theme }) => ({
  '& .MuiDataGrid-root': {
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      '&.cell-error': {
        backgroundColor: theme.palette.error.background,
        color: theme.palette.error.main,
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.total-row': {
        backgroundColor: theme.palette.info.lightBg,
        fontWeight: '500',
        '&:hover': {
          backgroundColor: theme.palette.info.lightBg,
        },
      },
      '&.subtotal-row': {
        fontWeight: '500',
      },
      '&.tax-row': {
        fontWeight: '500',
      },
      '&.normal-row': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiCircularProgress-root': {
      zIndex: 1,
    },
  },
}))

const BatchBillTicketTable = ({
  batchBillData,
  tickets,
  setGroupedTicketsData,
  hasEditedInboundTickets,
  setHasEditedInboundTickets,
  paymentMade,
  pricePrepend,
  setBillTotalValues,
  showPrintCheckButton,
  billData,
  paymentData,
  setAggregatedData,
  pdfPrint,
}) => {
  const dispatch = useDispatch()
  const apiRef = useGridApiRef()

  const firstTicket = tickets?.[0]
  const { customerId, customerName, billId, billExternalId } = firstTicket || {}
  const paymentId = paymentData?.id
  const { checkPaymentsData, validationSchema } = useMemo(() => {
    if (!billData || !paymentMade || !paymentId)
      return {
        checkPaymentsData: [],
        validationSchema: {},
      }
    return {
      checkPaymentsData: [{ id: paymentId, total: billData?.total }],
      validationSchema: {
        [paymentId]: Yup.object().shape({
          details: Yup.object().shape({
            payFrom: Yup.string().required('Pay From is Required'),
            checkNumber: Yup.string().required('Check Number is Required'),
          }),
        }),
      },
    }
  }, [paymentMade, paymentId, billData])

  const {
    handleSubmit: checkPrintHandleSubmit,
    control: checkPrintControl,
    reset: checkPrintReset,
    getValues: checkPrintGetValues,
    setValue: checkPrintSetValue,
    watch: checkPrintWatch,
    trigger: checkPrintTrigger,
    formState: { errors: checkPrintErrors, isValid: checkPrintIsValid },
  } = useForm({ resolver: yupResolver(Yup.object().shape(validationSchema)) })

  const [openPrintCheckBlade, setOpenPrintCheckBlade] = useState(false)

  const { enableMaterialPaymentHolds } = useAppSelector(state => state.featureFlags?.variables || {})

  const { defaultYardUm, showWeightDecimals } = useAppSelector(state => {
    const { defaultYardUm, settings } = state?.settings || {}
    return {
      defaultYardUm,
      showWeightDecimals: settings?.purchaseSettings?.inboundTickets,
    }
  }, shallowEqual)

  const { getDueDateFormatted } = usePaymentTerms({})

  const [updatePayment] = useUpdatePaymentMutation()

  const {
    data: customerData,
    error: customerError,
    refetch,
  } = useGetCustomerQuery({ customerId }, { skip: !customerId })

  const customerFailure = useRefetchAfterError(refetch, customerError)

  const updateAggregatedData = useCallback(newCustomerData => {
    setAggregatedData(previousAggregatedData => {
      const newAggregatedData = {
        ...previousAggregatedData,
      }
      if (!newAggregatedData[customerId]) {
        newAggregatedData[customerId] = {
          billData: null,
          customerData: null,
        }
      }
      newAggregatedData[customerId].customerData = newCustomerData
      return newAggregatedData
    })
  }, [])

  useEffect(() => {
    if (customerFailure) {
      updateAggregatedData({})
      dispatch(setCriticalDataLoadingError(true))
    }
  }, [customerFailure])

  useEffect(() => {
    if (!customerData) return
    updateAggregatedData(customerData)
  }, [customerData])

  const billTaxRate = useMemo(
    () => parseFloat(paymentMade ? billData?.taxRate : customerData?.taxRate) || 0,
    [paymentMade, billData, customerData],
  )

  const handleRemoveTicket = useCallback(
    ticketId => {
      const filteredTickets = tickets?.filter(ticket => ticket.id !== ticketId)
      if (filteredTickets?.length > 0) {
        setGroupedTicketsData(previousGroupedTicketsData => ({
          ...previousGroupedTicketsData,
          [customerId]: filteredTickets,
        }))
      } else {
        setGroupedTicketsData(previousGroupedTicketsData => {
          const newGroupedTicketsData = {
            ...previousGroupedTicketsData,
          }
          delete newGroupedTicketsData[customerId]
          return newGroupedTicketsData
        })
        setBillTotalValues(previousBillTotalValues => {
          const newBillTotalValues = {
            ...previousBillTotalValues,
          }
          delete newBillTotalValues[customerId]
          return newBillTotalValues
        })
      }
      setHasEditedInboundTickets(true)
    },
    [customerId, tickets],
  )

  const onPrintCheck = async () => {
    try {
      const checkingAccountId = checkPrintGetValues(`${paymentId}.details.payFrom`)
      const sanitizedCheckingAccountId = checkingAccountId === 'Main Checking Account' ? undefined : checkingAccountId
      const formattedData = {
        id: paymentId,
        billId: billData?.id,
        paymentDate: new Date(batchBillData.paymentDate).getTime(),
        completionDate: new Date(batchBillData.completionDate).getTime(),
        paymentMethod: batchBillData.paymentMethod,
        accountId: sanitizedCheckingAccountId,
        payToContactId: checkPrintGetValues(`${paymentId}.information.payToTheOrder`)?.id,
        payToLocationId: checkPrintGetValues(`${paymentId}.information.location`)?.id,
        total: {
          amount: (billData?.total || 0) * 100,
          currency: batchBillData?.currency,
          precision: 2,
        },
        inboundTickets: tickets?.map(inboundTicket => inboundTicket.id) || [],
        checkNumber: checkPrintGetValues(`${paymentId}.details.checkNumber`),
        isManualCheckNumber: true,
        checkMemo: checkPrintGetValues(`${paymentId}.information.memo`),
      }

      const { updatePaymentError } = await updatePayment({ paymentId: paymentId, payment: formattedData })
      if (updatePaymentError) throw new Error(updatePaymentError?.data?.error)
    } catch (error) {
      const message = 'An error occurred updating the payment'
      dispatchSnackbar(dispatch, 'error', message)
    }
  }

  const columns = [
    {
      field: 'ticketId',
      headerName: 'Ticket #',
      flex: 1,
      sortable: true,
      valueGetter: params => {
        return params.row.ticketId
      },
      sortComparator: excludePinnedRowsSort,
      renderCell: params => {
        const { id, ticketId, effectiveDate, materials, isInLockedAccountingPeriodV2 } = params.row
        if (pinnedRows.includes(id)) return ticketId
        const displayPaymentHold = materials?.some(material => {
          return (
            material.materialType?.rules?.some(rule => rule.type === MATERIAL_TYPE_RULES.PAYMENT_HOLD) &&
            paymentHoldInEffect(material.materialType?.rules, effectiveDate)
          )
        })
        return (
          <>
            <StyledLink
              to={`/app/purchases/ticket-library/${id}`}
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                window.open(`/app/purchases/ticket-library/${id}`, '_blank')
              }}
            >
              {ticketId}
            </StyledLink>
            {enableMaterialPaymentHolds && displayPaymentHold && (
              <Tooltip title="This ticket is on payment hold">
                <TimerSharpIcon color="warning" sx={{ ml: 1 }} />
              </Tooltip>
            )}
            {isInLockedAccountingPeriodV2 && (
              <Tooltip title={generateLockTooltip('inbound ticket')} placement="top">
                <LockIcon color="info" sx={{ ml: 1 }} />
              </Tooltip>
            )}
          </>
        )
      },
    },
    {
      field: 'effectiveDate',
      headerName: 'Date',
      flex: 1,
      sortable: false,
    },
    {
      field: 'orderNumber',
      headerName: 'PO#',
      flex: 1,
      sortable: true,
      valueGetter: params => {
        const { transportationInfo } = params.row
        return Big(transportationInfo?.purchaseOrderNumber || 0).toNumber()
      },
      sortComparator: excludePinnedRowsSort,
      renderCell: params => {
        const { purchaseOrderNumber, purchaseOrderId } = params.row.transportationInfo || {}
        if (pinnedRows.includes(params.row.id)) return ''
        if (!purchaseOrderNumber || !purchaseOrderId) return '-'
        return (
          <StyledLink
            to={`/app/contracts/purchase-orders/${purchaseOrderId}`}
            onClick={event => {
              event.preventDefault()
              event.stopPropagation()
              window.open(`/app/contracts/purchase-orders/${purchaseOrderId}`, '_blank')
            }}
          >
            {purchaseOrderNumber}
          </StyledLink>
        )
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      sortable: false,
    },
    {
      field: 'netWeight',
      headerName: 'Net Weight',
      headerAlign: 'right',
      align: 'right',
      flex: 1,
      valueGetter: params => {
        const { id, netWeight } = params.row
        if (id === 'tax') return ''
        return `${setDecimalPrecision(netWeight, {
          removeTrailingZeros: true,
          decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
        })} ${defaultYardUm}`
      },
      sortable: false,
    },
    {
      field: 'dueDate',
      headerName: 'Due Date',
      flex: 1,
      valueGetter: params => {
        const { id, effectiveDate } = params.row
        if (pinnedRows.includes(id)) return ''
        return getDueDateFormatted(effectiveDate, customerData?.defaultPaymentTermId)
      },
      sortable: false,
    },
    {
      field: 'value',
      headerName: 'Value',
      headerAlign: 'right',
      align: 'right',
      flex: 1,
      valueGetter: params =>
        `${pricePrepend}${setDecimalPrecision(params.row.value || 0, { decimalPrecisionOverride: 2 })}`,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => {
        const { id } = params.row
        // Don't show delete button for summary rows or when batch bill is paid
        if (pinnedRows.includes(id) || paymentMade) {
          return null
        }
        return (
          <Tooltip title="Remove ticket from bill">
            <IconButton
              size="small"
              onClick={() => handleRemoveTicket(id)}
              color="error"
              data-test={`remove-ticket-${id}`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      },
    },
  ]

  useEffect(() => {
    if (tickets.length > 0 && apiRef?.current?.updateRows) {
      apiRef?.current?.updateRows([
        { id: 'subtotal', _action: 'delete' },
        { id: 'tax', _action: 'delete' },
        { id: 'total', _action: 'delete' },
      ])

      const aggregatedData = tickets.reduce(
        (aggregate, ticket) => {
          const ticketSubtotal = ticket?.value || 0
          return {
            net: Big(aggregate.net)
              .plus(Big(ticket?.netWeight || 0))
              .round(ROUNDING_PRECISIONS.WEIGHT)
              .toNumber(),
            subtotal: Big(aggregate.subtotal)
              .plus(Big(ticketSubtotal || 0))
              .round(ROUNDING_PRECISIONS.COST)
              .toNumber(),
          }
        },
        { net: 0, subtotal: 0 },
      )

      const subtotalValue = Big(aggregatedData.subtotal)
        .plus(Big(billData?.appliedRoundingDelta || 0))
        .round(ROUNDING_PRECISIONS.COST)
        .toNumber()
      const taxValue = Big(subtotalValue).mul(Big(billTaxRate)).div(100).round(ROUNDING_PRECISIONS.COST).toNumber()
      const totalValue = Big(subtotalValue).plus(Big(taxValue)).round(ROUNDING_PRECISIONS.COST).toNumber()

      setBillTotalValues(previousBillTotalValues => ({
        ...previousBillTotalValues,
        [customerId]: totalValue,
      }))

      setTimeout(() => {
        const newRows = [
          {
            id: 'subtotal',
            ticketId: 'Subtotal',
            netWeight: aggregatedData.net,
            value: subtotalValue,
          },
          {
            id: 'tax',
            ticketId: 'Tax',
            value: taxValue,
          },
          {
            id: 'total',
            ticketId: 'Total',
            netWeight: aggregatedData.net,
            value: totalValue,
          },
        ]
        apiRef?.current?.updateRows(taxValue === 0 ? newRows.slice(2) : newRows)
      }, 0)
    }
  }, [apiRef, billData, billTaxRate, tickets, customerId])

  const getCellClassName = params => {
    if (
      params.field === 'ticketId' &&
      !pinnedRows.includes(params.row.id) &&
      materialsHasTareRequiredAndMissing(params.row?.materials)
    )
      return 'cell-error'
    return ''
  }

  const selectedTicketsDataArray = useMemo(() => {
    return tickets ? [tickets] : []
  }, [tickets])

  const customersDataArray = useMemo(() => {
    return customerData ? [customerData] : []
  }, [customerData])

  const startingCheckNumberOverrides = useMemo(() => {
    return paymentData ? [paymentData.checkNumber] : []
  }, [paymentData])

  return (
    <>
      <Stack direction="row" mt={4} mb={1} justifyContent="space-between">
        <Stack direction="row" spacing={1}>
          <Typography variant="body1" color="textSecondary">
            {customerName}
          </Typography>
          {hasEditedInboundTickets ? null : (
            <StyledLink
              to={`/app/purchases/bills/${billId}`}
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                window.open(`/app/purchases/bills/${billId}`, '_blank')
              }}
              style={{ fontWeight: 'unset' }}
            >
              {`Bill #${billExternalId}`}
            </StyledLink>
          )}
        </Stack>
        {hasEditedInboundTickets ? null : (
          <Stack direction="row" spacing={1}>
            {showPrintCheckButton && checkPaymentsData.length > 0 ? (
              <Button size="small" variant="contained" onClick={() => setOpenPrintCheckBlade(true)}>
                PRINT CHECK
              </Button>
            ) : null}
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() =>
                pdfPrint(billData, `/core-api/purchases/bills/${billId}/pdf/${PDF_TYPE.BILL_SETTLEMENT_DOC}`)
              }
            >
              PRINT
            </Button>
          </Stack>
        )}
      </Stack>
      <GridStyling>
        <BasicDataGrid
          density="compact"
          columns={columns}
          rows={tickets}
          apiRef={apiRef}
          disableColumnMenu
          hideFooter
          getRowClassName={params => (pinnedRows.includes(params.row.id) ? `${params.row.id}-row` : 'normal-row')}
          getCellClassName={params => getCellClassName(params)}
          pinnedRows={{
            bottom: [{ id: 'subtotal' }, { id: 'tax' }, { id: 'total' }],
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'ticketId', sort: 'asc' }],
            },
          }}
          onSortModelChange={model => {
            if (model.length === 0) {
              apiRef?.current?.setSortModel([{ field: 'ticketId', sort: 'asc' }])
            }
          }}
        />
      </GridStyling>
      <PrintCheckBlade
        open={openPrintCheckBlade}
        setOpen={setOpenPrintCheckBlade}
        checkPaymentsData={checkPaymentsData}
        selectedTicketsData={selectedTicketsDataArray}
        customersData={customersDataArray}
        handleSubmit={checkPrintHandleSubmit}
        setValue={checkPrintSetValue}
        watch={checkPrintWatch}
        trigger={checkPrintTrigger}
        control={checkPrintControl}
        reset={checkPrintReset}
        errors={checkPrintErrors}
        isValid={checkPrintIsValid}
        onPrint={async () => {
          await onPrintCheck()
          setOpenPrintCheckBlade(false)
          pdfPrint(billData, `/core-api/purchases/payments/${billId}/pdf/${PDF_TYPE.CHECK}`, '', PDF_TYPE.CHECK)
        }}
        pricePrepend={pricePrepend}
        isBatchBill
        startingCheckNumberOverrides={startingCheckNumberOverrides}
      />
    </>
  )
}

export default BatchBillTicketTable
